import React from 'react';

import { Stack } from '@/components/layout';
import { Button, PressableSurface } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

/** Translucent white shown behind the back arrow when iOS Liquid Glass isn't available. */
const BACK_GLASS_FALLBACK = 'rgba(255, 255, 255, 1)';
const BACK_GLASS = 'rgba(255, 255, 255, 0)';

type OnboardingFooterProps = {
  onContinue: () => void;
  /** Show a secondary "Back" button on the left (hidden on the first step). */
  showBack?: boolean;
  onBack?: () => void;
  /** Override the primary button label (e.g. "Finish" on the last step). */
  continueLabel?: string;
};

/**
 * Fixed action bar pinned to the bottom of the onboarding wizard. Renders the primary
 * "Continue" button (right-aligned, matching Figma) and an optional "Back" button.
 */
export function OnboardingFooter({
  onContinue,
  showBack = false,
  onBack,
  continueLabel,
}: OnboardingFooterProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const strings = STRINGS.onboarding;

  return (
    <Stack
      direction="row"
      align="center"
      justify={showBack ? 'space-between' : 'flex-end'}
      gap={scale(12)}
      style={styles.container}>
      {showBack ? (
        <PressableSurface
          onPress={onBack}
          size={48}
          borderRadius={12}
          variant="glass"
          glassEffect="regular"
          backgroundColor={BACK_GLASS_FALLBACK}
          glassTintColor={BACK_GLASS}
          style={styles.backButton}
          accessibilityLabel={strings.buttonBack}>
          <PressableSurface.Icon
            name="IcArrowRight"
            size={20}
            color={guardianColors['Main/Main']}
            style={styles.backIcon}
          />
        </PressableSurface>
      ) : null}

      <Button onPress={onContinue} style={styles.continueButton}>
        <Button.Text
          variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
          style={styles.continueLabel}>
          {continueLabel ?? strings.buttonContinue}
        </Button.Text>
        <Button.Icon name="IcArrowRight" position="right" size={scale(20)} color={colors['0']} />
      </Button>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  container: {
    width: '100%',
  },
  // 48x48 glass surface (white border + soft shadow) matching Figma. The shadow
  // sits on the Pressable wrapper so the glass view's clipped corners don't crop it.
  backButton: {
    borderRadius: scale(12),
    shadowColor: '#292757',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 1,
    elevation: 2,
  },
  // The arrow icon ships pointing right; mirror it to point back.
  backIcon: {
    transform: [{ scaleX: -1 }],
  },
  continueButton: {
    minWidth: scale(140),
    height: scale(48),
    borderRadius: scale(12),
    backgroundColor: guardianColors['Main/Main'],
  },
  continueLabel: {
    color: colors['0'],
  },
}));

export default OnboardingFooter;
